import styled from '@emotion/native';
import React, { useCallback } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { Entypo } from '@expo/vector-icons';
import { BaseText } from './StyledText';
import { Href, router } from 'expo-router';
import { sand, welcomeMutedOnGreen } from '../styles/colors';
import { spacing, typography } from '../styles/constants';
import { IS_WEB } from '../constants/utility';
import { useWebHoverToggle } from '../hooks/useWebHoverToggle';

export interface BackButtonProps {
  containerStyle?: StyleProp<ViewStyle>;
  tintColor?: string;
  /** When set, used if `router.canGoBack()` is false (e.g. direct link to this screen). */
  fallbackHref?: Href;
}

const BackButton = ({
  containerStyle,
  tintColor = welcomeMutedOnGreen,
  fallbackHref,
}: BackButtonProps) => {
  const { isHovered, webHoverProps } = useWebHoverToggle();
  const effectiveTint = IS_WEB && isHovered ? sand : tintColor;
  const handlePress = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }
    if (fallbackHref != null) {
      router.push(fallbackHref);
    }
  }, [fallbackHref]);
  return (
    <BackButtonContainer
      onPress={handlePress}
      style={containerStyle}
      accessibilityRole="button"
      accessibilityLabel="Go back"
      {...webHoverProps}
    >
      <ChevronWrap accessible={false}>
        <Entypo
          name="chevron-thin-left"
          size={typography.fontSize.xs}
          color={effectiveTint}
        />
      </ChevronWrap>
      <BackLinkLabel labelColor={effectiveTint}>Back</BackLinkLabel>
    </BackButtonContainer>
  );
};

const BackButtonContainer = styled.TouchableOpacity({
  flexDirection: 'row',
  alignItems: 'center',
});

const ChevronWrap = styled.View({
  marginRight: spacing.small,
  justifyContent: 'center',
  alignItems: 'center',
});

const BackLinkLabel = styled(BaseText)<{ labelColor: string }>(
  ({ labelColor }) => ({
    color: labelColor,
    fontSize: typography.fontSize.sm,
    fontWeight: '400',
    marginBottom: 0,
  }),
);

export default BackButton;
